<template>
  <ProductTitledBlock
    v-if="relatedAssets && relatedAssets.length > 0"
    class="mb-8 mt-6 md:pr-[30px] print:mx-0"
    icon=""
    :title="model?.title || $t('custom.product_details.documents.title')"
  >
    <!-- Action bar -->
    <div
      v-if="selectedCount > 0 && storeId === 'HerasIntern'"
      class="mb-4 flex flex-col xl:flex-row items-center rounded-md bg-primary-50 px-4 py-2"
    >
      <div class="flex items-center">
        <div
          class="flex h-6 w-6 items-center justify-center rounded-md border-2 border-primary-500 cursor-pointer mr-2"
          @click="removeSelected()"
        >
          <VcIcon name="checkmark-no-bg" class="text-primary-500 top-[2px] left-[0.5px] relative" :size="12" />
        </div>

        <span class="font-light md:pr-2">
          {{ selectedCount }} {{ $t("custom.product_details.documents.selected") }}</span
        >
      </div>

      <div class="flex flex-col xl:flex-row gap-1 xl:gap-2 items-center">
        <template v-if="containsImageFile()">
          <div class="hidden xl:block w-[1px] h-[25px] bg-primary-500 mx-2"></div>
          <VcButton
            class="px-0"
            variant="no-background"
            size="sm"
            color="primary"
            prepend-icon="download"
            @click="downloadSelected(960, 400)"
          >
            {{ $t("custom.product_details.documents.960") }}
          </VcButton>

          <div class="hidden xl:block w-[1px] h-[25px] bg-primary-500 mx-2"></div>
          <VcButton
            class="px-0"
            variant="no-background"
            prepend-icon="download"
            size="sm"
            color="primary"
            @click="downloadSelected(2400, 1400)"
          >
            {{ $t("custom.product_details.documents.2400") }}
          </VcButton>

          <div>
            <div class="flex gap-1 items-center">
              <div class="hidden xl:block w-[1px] h-[25px] bg-primary-500 mx-2"></div>

              <VcButton
                class="px-0"
                variant="no-background"
                prepend-icon="download"
                size="sm"
                color="primary"
                :disabled="!meta.valid"
                @click="downloadSelected(width, height)"
              >
                {{ width }}<span>x</span>{{ height }}
              </VcButton>

              <VcInput
                v-model="width"
                class="w-[60px] h-[30px] mr-1"
                type="number"
                required
                min="0"
                :max="4500"
                @update:model-value="
                  $nextTick(() => {
                    width = validateMaxSize(+$event!, 4500);
                  })
                "
                :maxlength="64"
              />
              <VcInput
                v-model="height"
                class="w-[60px] h-[30px]"
                type="number"
                min="0"
                :max="3000"
                @update:model-value="
                  $nextTick(() => {
                    height = validateMaxSize(+$event!, 3000);
                  })
                "
                required
                :maxlength="64"
              />
            </div>
          </div>
        </template>
        <template v-else>
          <div class="hidden xl:block w-[1px] h-[25px] bg-primary-500 mx-2"></div>
          <VcButton
            class="px-0"
            variant="no-background"
            size="sm"
            color="primary"
            prepend-icon="download"
            @click="downloadSelected(1024, 1024)"
          >
            {{ $t("custom.product_details.documents.download") }}
          </VcButton>
        </template>
      </div>
    </div>

    <!-- Grid -->
    <div class="grid grid-cols-2 gap-4 lg:grid-cols-3">
      <template v-for="item in relatedAssets" :key="item.id">
        <template v-for="asset in item.assets" :key="asset.id">
          <div
            v-if="asset.url"
            class="relative cursor-pointer rounded-lg border bg-[#F8F8F8] px-4 pt-4 transition"
            :class="isSelected(asset) && storeId === 'HerasIntern' ? 'ring-2 ring-primary' : ''"
            @click="toggleAsset(asset)"
          >
            <!-- Checkbox -->
            <template v-if="storeId === 'HerasIntern'">
              <label class="absolute z-0 left-2 top-2 inline-flex items-center cursor-pointer select-none">
                <input type="checkbox" class="peer hidden" :checked="isSelected(asset)" @click="toggleAsset(asset)" />

                <span
                  class="flex h-5 w-5 items-center justify-center rounded border-2 bg-white border-neutral-500 peer-checked:bg-primary-500 peer-checked:border-primary-500 transition-colors"
                >
                  <VcIcon
                    v-if="isSelected(asset)"
                    name="checkmark-no-bg"
                    class="h-4 w-4 top-[2px] left-[0.5px] relative text-white"
                    :size="12"
                  />
                </span>
              </label>
            </template>

            <div class="bg-white">
              <!-- Image -->
              <VcImage
                v-if="item?.images?.[0]?.url"
                :src="item.images[0].url"
                size-suffix="sm"
                lazy
                class="h-[150px] w-full object-contain"
                :alt="asset.name"
              />
              <VcImage
                v-else
                :src="NO_IMAGE_URL"
                size-suffix="sm"
                lazy
                class="h-[150px] w-full object-contain"
                :alt="asset.name"
              />
            </div>
          </div>
        </template>
      </template>
    </div>
  </ProductTitledBlock>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { NO_IMAGE_URL } from "@/core/constants";
import { globals } from "@/core/globals";
import { downloadZipWithNewClient } from "@/shared/files";
import ProductTitledBlock from "../product-titled-block.vue";
import type { Product, Asset } from "@/core/api/graphql/types";
import { AssetOperationAction, JpegQuality } from "@/core/api/graphql/types";
import type { PropType } from "vue";
import { useAssetOperations } from "@/shared/catalog";
import { toTypedSchema } from "@vee-validate/yup";
import { useForm, useField } from "vee-validate";
import { object, number } from "yup";

const props = defineProps({
  model: {
    type: Object,
  },
  relatedAssets: {
    type: Array as PropType<Product[]>,
    required: true,
  },
  productName: {
    type: String,
  },
  sku: {
    type: String,
  },
});

const validationSchema = toTypedSchema(
  object({
    width: number().max(4500).required(),
    height: number().max(3000).required(),
  }),
);

const { meta } = useForm({
  validationSchema,
  initialValues: {
    width: 0,
    height: 0,
  },
});

const { value: width } = useField<number>("width");
const { value: height } = useField<number>("height");

const relatedAssets = computed(() => props.relatedAssets);
const { storeId } = globals;
const { downloadAssets } = useAssetOperations();

const selectedAssets = ref<Set<Asset>>(new Set());
const selectedCount = computed(() => selectedAssets.value.size);

function toggleAsset(asset: Asset) {
  if (storeId === "HerasIntern") {
    if (selectedAssets.value.has(asset)) {
      selectedAssets.value.delete(asset);
    } else {
      selectedAssets.value.add(asset);
    }
  } else {
    open(asset.url, "_blank");
  }
}

function isSelected(asset: Asset) {
  return selectedAssets.value.has(asset);
}

function removeSelected() {
  selectedAssets.value = new Set();
}

function containsImageFile(): boolean {
  return Array.from(selectedAssets.value).some((file) => file.mimeType?.startsWith("image/"));
}

function isImageFile(file: string | undefined): boolean {
  return !!file && file?.startsWith("image/");
}

async function downloadSelected(fileWidth: number, fileHeight: number) {
  const mappedAssets = Array.from(selectedAssets.value).map((item) => ({
    blobUrl: item.url,
    width: fileWidth,
    height: fileHeight,
    action: isImageFile(item.mimeType) ? AssetOperationAction.Resize : AssetOperationAction.None,
    quality: JpegQuality.High,
    maintainAspectRation: isImageFile(item.mimeType),
  }));

  const response = await downloadAssets(mappedAssets);

  const downloadName = props.productName! + "_" + props.sku;

  if (response?.blobUrl) {
    await downloadZipWithNewClient(response.blobUrl, downloadName);
  }
}

function validateMaxSize(value: number, max: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }

  if (value < 0) {
    return 0;
  }

  if (value > max) {
    return max;
  }

  return value;
}
</script>
